// Phase 84.M.94 — Modal chặn cứng buộc VĐV xử lý đề xuất điểm trình từ Admin.
//
// Spec (user choice 5a, 6a): chỉ chặn ở `/athlete/*`, KHÔNG nút đóng. 2 lựa
// chọn duy nhất: (A) Chấp nhận điểm admin gửi xuống → auto rating_verified=true,
// hoặc (B) Đề xuất lại 1 điểm khác kèm lý do — admin sẽ nhận Telegram + badge.
//
// Sau khi user accept hoặc counter, BE chuyển status → accepted/countered →
// gate context refetch + modal tự đóng. Trong thời gian chờ refresh, button
// disabled.
import { useState } from "react";
import { toast } from "sonner";
import {
  ShieldCheck,
  CheckCircle,
  ArrowsClockwise,
  Warning,
  Spinner,
  Lightning,
} from "@phosphor-icons/react";
import { athleteRatingProposals } from "../../../lib/athleteApi";
import { formatApiErrorDetail } from "../../../lib/authApi";

export default function PendingRatingProposalForceModal({ open, proposal, onResolved }) {
  const [mode, setMode] = useState("choose"); // choose | accept | counter
  const [ack, setAck] = useState(false);
  const [counterScore, setCounterScore] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  if (!open || !proposal) return null;

  const proposedRating = Number(proposal.proposed_rating);
  const currentRating = proposal.current_rating != null
    ? Number(proposal.current_rating)
    : null;
  const proposedBy = proposal.proposed_by_name || "BTC";
  const reasonFromAdmin = proposal.reason || "";

  const doAccept = async () => {
    if (!ack) {
      toast.error("Vui lòng tích cam kết trước khi chấp nhận");
      return;
    }
    setBusy(true);
    try {
      await athleteRatingProposals.decide(proposal.id, "accepted", true);
      toast.success(`Đã chấp nhận điểm trình ${proposedRating.toFixed(2)}`);
      onResolved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Lỗi xử lý");
    } finally {
      setBusy(false);
    }
  };

  const doCounter = async () => {
    if (!ack) {
      toast.error("Vui lòng tích cam kết trước khi đề xuất lại");
      return;
    }
    const val = Number(counterScore);
    if (!Number.isFinite(val) || val < 2 || val > 8) {
      toast.error("Điểm đề xuất phải nằm trong khoảng 2.00 – 8.00");
      return;
    }
    if (Math.abs(val - proposedRating) < 0.001) {
      toast.error("Điểm đề xuất trùng với điểm admin. Vui lòng chọn 'Chấp nhận'.");
      return;
    }
    if (!reason.trim() || reason.trim().length < 5) {
      toast.error("Vui lòng nhập lý do (≥ 5 ký tự)");
      return;
    }
    setBusy(true);
    try {
      await athleteRatingProposals.counter(proposal.id, val, reason.trim(), true);
      toast.success("Đã gửi đề xuất ngược lại — BTC sẽ xem xét");
      onResolved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Lỗi xử lý");
    } finally {
      setBusy(false);
    }
  };

  // Phase 85.A.172 — Force-ack mode: admin đã chốt sau khi VĐV counter.
  // Chỉ 1 lựa chọn duy nhất "Tôi đã hiểu" — backend apply điểm BTC ngay.
  const isForceAck = !!proposal?.requires_force_ack;

  const doForceAck = async () => {
    setBusy(true);
    try {
      const r = await athleteRatingProposals.forceAck(proposal.id);
      const applied = Number(r?.applied_score || proposedRating || 0);
      toast.success(`BTC đã áp dụng điểm trình ${applied.toFixed(2)} cho bạn.`);
      onResolved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Lỗi xử lý");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[145] flex items-end sm:items-center justify-center bg-black/85 backdrop-blur-md"
      data-testid="rating-proposal-force-modal"
    >
      <div className="w-full sm:max-w-md max-h-[92dvh] overflow-y-auto bg-gradient-to-br from-[#0B1220] to-[#0F1A2E] border-t sm:border border-amber-400/30 sm:rounded-3xl rounded-t-3xl shadow-2xl">
        <div className="p-5 pb-6 space-y-4">
          {/* Hero */}
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 shrink-0 rounded-2xl bg-gradient-to-br from-amber-400 to-amber-500 flex items-center justify-center shadow-[0_8px_24px_rgba(251,191,36,0.45)]">
              <Lightning size={22} weight="fill" className="text-[#1A140C]" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-black tracking-[0.25em] text-amber-300/85 uppercase">
                {isForceAck ? "Quyết định cuối · BTC đã chốt" : "Bắt buộc · Xử lý đề xuất"}
              </div>
              <h2 className="text-[19px] font-black text-white leading-tight mt-1">
                {isForceAck ? "BTC đã chốt điểm trình của bạn" : "BTC vừa đề xuất điểm trình mới"}
              </h2>
              <p className="text-[12.5px] text-white/70 mt-1.5 leading-relaxed">
                {isForceAck
                  ? <>Bạn đã đề xuất ngược, BTC đã xem xét và đưa ra điểm cuối cùng. Bấm <b className="text-amber-200">"Tôi đã hiểu"</b> để áp dụng điểm này — không thể đề xuất tiếp.</>
                  : <>Bạn cần CHẤP NHẬN hoặc ĐỀ XUẤT LẠI để tiếp tục sử dụng. Popup sẽ hiển thị mỗi khi vào trang đến khi hoàn tất.</>
                }
              </p>
            </div>
          </div>

          {/* Proposal info */}
          <div className="rounded-2xl border border-amber-400/30 bg-amber-500/8 p-4 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-[10.5px] uppercase tracking-widest text-white/55 font-black">
                BTC đề xuất
              </span>
              <span className="text-[10.5px] text-white/55 font-bold">
                {proposedBy}
              </span>
            </div>
            <div className="flex items-baseline gap-3">
              {currentRating != null && (
                <span className="text-[15px] text-white/55 line-through tabular-nums">
                  {currentRating.toFixed(2)}
                </span>
              )}
              <span className="text-[36px] font-black text-amber-300 tabular-nums leading-none">
                {proposedRating.toFixed(2)}
              </span>
            </div>
            {reasonFromAdmin && (
              <div className="rounded-lg bg-black/30 border border-white/8 px-3 py-2 text-[11.5px] text-white/75 italic">
                "{reasonFromAdmin}"
              </div>
            )}
          </div>

          {/* Mode picker */}
          {!isForceAck && mode === "choose" && (
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => setMode("accept")}
                className="w-full h-14 rounded-2xl bg-gradient-to-r from-emerald-500 to-emerald-400 text-[#062014] font-black text-[13.5px] tracking-wide shadow-[0_8px_24px_rgba(16,185,129,0.4)] inline-flex items-center justify-center gap-2"
                data-testid="rating-force-pick-accept"
              >
                <CheckCircle size={16} weight="fill" />
                Chấp nhận {proposedRating.toFixed(2)}
              </button>
              <button
                type="button"
                onClick={() => setMode("counter")}
                className="w-full h-14 rounded-2xl bg-gradient-to-r from-sky-500 to-cyan-400 text-[#04162B] font-black text-[13.5px] tracking-wide shadow-[0_8px_24px_rgba(56,189,248,0.4)] inline-flex items-center justify-center gap-2"
                data-testid="rating-force-pick-counter"
              >
                <ArrowsClockwise size={16} weight="bold" />
                Đề xuất lại điểm khác
              </button>
            </div>
          )}

          {/* Phase 85.A.172 — Force-ack mode: 1 nút duy nhất */}
          {isForceAck && (
            <div className="space-y-3" data-testid="rating-force-ack-block">
              <div className="rounded-2xl border border-amber-400/40 bg-amber-500/[0.08] p-3.5 text-amber-100/95 text-[12px] leading-relaxed">
                <div className="font-black text-[12.5px] mb-1 flex items-center gap-1.5">
                  <Warning size={13} weight="fill" className="text-amber-300" />
                  Lưu ý quan trọng
                </div>
                Sau khi bạn đề xuất ngược, BTC đã đưa ra điểm cuối cùng. Đây là quyết định không thể thương lượng tiếp. Bấm "Tôi đã hiểu" để hoàn tất.
              </div>
              <button
                type="button"
                onClick={doForceAck}
                disabled={busy}
                className="w-full h-14 rounded-2xl bg-gradient-to-r from-[#FF8A00] to-[#FF9F1C] hover:shadow-[0_8px_24px_rgba(255,138,0,0.55)] text-white font-black text-[14px] tracking-wide inline-flex items-center justify-center gap-2 active:scale-[0.98] disabled:opacity-60"
                data-testid="rating-force-ack-confirm"
              >
                {busy ? <Spinner size={16} className="animate-spin" /> : <CheckCircle size={16} weight="fill" />}
                {busy ? "Đang áp dụng…" : "Tôi đã hiểu — Áp dụng điểm"}
              </button>
            </div>
          )}

          {/* Accept flow */}
          {!isForceAck && mode === "accept" && (
            <div className="space-y-3" data-testid="rating-force-accept-block">
              <label className="block rounded-2xl border border-white/15 bg-white/[0.04] p-3 cursor-pointer">
                <div className="flex items-start gap-2.5">
                  <input
                    type="checkbox"
                    checked={ack}
                    onChange={(e) => setAck(e.target.checked)}
                    className="mt-0.5 w-4 h-4 accent-emerald-400"
                    data-testid="rating-force-accept-ack"
                  />
                  <div className="text-[12px] text-white/85 leading-relaxed">
                    <div className="font-black text-white mb-0.5 flex items-center gap-1.5">
                      <ShieldCheck size={12} weight="fill" className="text-emerald-300" />
                      Cam kết
                    </div>
                    Tôi đồng ý nhận điểm trình {proposedRating.toFixed(2)} và chịu trách nhiệm về thông tin trình độ.
                  </div>
                </div>
              </label>

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => { setMode("choose"); setAck(false); }}
                  disabled={busy}
                  className="flex-1 h-11 rounded-xl bg-white/8 border border-white/15 text-white font-bold text-[12.5px] disabled:opacity-40"
                  data-testid="rating-force-accept-back"
                >
                  Quay lại
                </button>
                <button
                  type="button"
                  onClick={doAccept}
                  disabled={!ack || busy}
                  className="flex-[2] h-11 rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-400 text-[#062014] font-black text-[13px] disabled:opacity-40 inline-flex items-center justify-center gap-2"
                  data-testid="rating-force-accept-confirm"
                >
                  {busy ? (
                    <><Spinner size={14} className="animate-spin" /> Đang xử lý…</>
                  ) : (
                    <><CheckCircle size={14} weight="fill" /> Chấp nhận</>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Counter flow */}
          {!isForceAck && mode === "counter" && (
            <div className="space-y-3" data-testid="rating-force-counter-block">
              <label className="block">
                <span className="block text-[10.5px] uppercase tracking-widest text-white/55 font-black mb-1.5">
                  Điểm bạn muốn đề xuất lại
                </span>
                <input
                  type="number"
                  step="0.01"
                  min="2.00"
                  max="8.00"
                  value={counterScore}
                  onChange={(e) => setCounterScore(e.target.value)}
                  placeholder="VD: 2.85"
                  className="w-full h-12 px-4 rounded-xl bg-[#0F1623] border border-white/15 text-white text-[18px] font-black tabular-nums tracking-wider outline-none focus:border-sky-400/60"
                  data-testid="rating-force-counter-score"
                />
              </label>

              <label className="block">
                <span className="block text-[10.5px] uppercase tracking-widest text-white/55 font-black mb-1.5">
                  Lý do <span className="text-rose-300">*</span>
                </span>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={3}
                  maxLength={500}
                  placeholder="VD: Tôi vừa thắng giải địa phương, đề xuất điểm cao hơn…"
                  className="w-full px-3 py-2 rounded-xl bg-[#0F1623] border border-white/15 text-white text-[13px] outline-none focus:border-sky-400/60 resize-none"
                  data-testid="rating-force-counter-reason"
                />
                <div className="text-[10px] text-white/40 mt-1">
                  {reason.length}/500 ký tự (tối thiểu 5)
                </div>
              </label>

              <label className="block rounded-2xl border border-white/15 bg-white/[0.04] p-3 cursor-pointer">
                <div className="flex items-start gap-2.5">
                  <input
                    type="checkbox"
                    checked={ack}
                    onChange={(e) => setAck(e.target.checked)}
                    className="mt-0.5 w-4 h-4 accent-sky-400"
                    data-testid="rating-force-counter-ack"
                  />
                  <div className="text-[12px] text-white/85 leading-relaxed">
                    Tôi cam kết điểm đề xuất là trung thực và chịu trách nhiệm.
                  </div>
                </div>
              </label>

              {!Number.isFinite(Number(counterScore)) || Number(counterScore) <= 0 ? null : Math.abs(Number(counterScore) - proposedRating) < 0.001 ? (
                <div className="rounded-xl border border-amber-500/30 bg-amber-500/8 px-3 py-2 flex items-start gap-2 text-[11.5px] text-amber-200">
                  <Warning size={13} weight="bold" className="mt-0.5 shrink-0" />
                  <span>Điểm bạn nhập trùng admin gửi. Vui lòng chọn "Chấp nhận".</span>
                </div>
              ) : null}

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => { setMode("choose"); setAck(false); }}
                  disabled={busy}
                  className="flex-1 h-11 rounded-xl bg-white/8 border border-white/15 text-white font-bold text-[12.5px] disabled:opacity-40"
                  data-testid="rating-force-counter-back"
                >
                  Quay lại
                </button>
                <button
                  type="button"
                  onClick={doCounter}
                  disabled={busy}
                  className="flex-[2] h-11 rounded-xl bg-gradient-to-r from-sky-500 to-cyan-400 text-[#04162B] font-black text-[13px] disabled:opacity-40 inline-flex items-center justify-center gap-2"
                  data-testid="rating-force-counter-confirm"
                >
                  {busy ? (
                    <><Spinner size={14} className="animate-spin" /> Đang gửi…</>
                  ) : (
                    <><ArrowsClockwise size={14} weight="bold" /> Gửi đề xuất lại</>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
